import { computed, type ComputedRef } from 'vue';
import { useFirebaseStore } from 'freedom-shared';
import type { Airflow } from '@aviata/platform-types';
import type { flightEntity } from 'freedom-shared';

type CampaignPromocode = {
  code: string;
  airlines: string[];
  isDomestic: boolean;
};

export const useCampaignPromocode = (
  flights: ComputedRef<flightEntity.types.MappedFlight[]>,
  bookings: ComputedRef<Airflow.Product.BookingData[]>,
  hasActivePromocode: ComputedRef<boolean>,
) => {
  const { serializedRemoteConfig, isLoading } = useFirebaseStore();

  const campaignPromocode = computed(
    () =>
      serializedRemoteConfig?.avfree_campaign_promocode as CampaignPromocode,
  );

  const hasNecessaryAirlineForCampaignPromocode = () => {
    const necessaryAirlines = campaignPromocode.value?.airlines;
    const currentAirlines = flights.value?.flatMap(flight =>
      flight.segments.map(segment => segment.airline),
    );
    return (
      necessaryAirlines &&
      (necessaryAirlines.length === 0 ||
        currentAirlines?.some(airline => necessaryAirlines.includes(airline)))
    );
  };

  const isDirectionTypeOk = () => {
    const isDomestic = bookings.value?.every(
      booking => booking.offer.$meta.is_domestic,
    );

    return isDomestic === campaignPromocode.value?.isDomestic;
  };

  const shouldApplyCampaignPromocode = () => {
    return (
      !hasActivePromocode.value &&
      campaignPromocode.value?.code &&
      hasNecessaryAirlineForCampaignPromocode() &&
      isDirectionTypeOk()
    );
  };

  return {
    shouldApplyCampaignPromocode,
    value: computed(() => campaignPromocode.value?.code),
    isLoading,
  };
};
